<template><div><!-- Note the date must be in this format YYYY-M-D and wrapped in single quotes -->
<BlogPost :author="$frontmatter.author" :posted="parse($frontmatter.posted, 'y-M-d', new Date())" :heading="$frontmatter.heading">
<p class="d-docsite--paragraph">Hello and happy Friday to you all as we approach the holiday season! 🎄 🕎</p>
<p class="d-docsite--paragraph">We'll be performing a number of redesigns to existing Dialtone components over the coming months. Some of these updates may involve breaking changes to existing Vue components and Dialtone classes. Today's update involves badge which we have just reworked for better styling, functionality and convenience.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><a href="https://dialtone.dialpad.com/components/badge.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Badge</a></li>
<li class="d-docsite--list-element"><a href="https://dialtone.dialpad.com/vue/?path=/story/components-badge--default" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Badge Vue Component</a></li>
</ul>
<h2 id="badge" tabindex="-1" class="d-docsite--header-2">Badge <a class="header-anchor d-link d-docsite--link d-link" href="#badge" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The recent updates to badge cause breaking changes in Dialtone v7.9.0 and Dialtone Vue v2.43.0 and Dialtone Vue 3 v3.29.0</p>
<p class="d-docsite--paragraph">The migration has been performed for all existing <code v-pre>DtBadge</code> components and <code v-pre>d-badge</code> classes in Dialpad and Dialpad Meetings. However if you have an existing feature branch or are in a project outside of Dialpad / Dialpad Meetings that involves badge, the changes will not have been made there so you'll have to make the updates yourself. If you need to do this, please follow the badge section of the <a href="https://github.com/dialpad/dialtone/blob/staging/migration_guide/MIGRATION_GUIDE.md#badge" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">migration guide</a>.</p>
<h3 id="variants" tabindex="-1" class="d-docsite--header-3">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h3>
<code-well-header>
<dt-stack direction="row" gap="400" align="center">
  <dt-badge text="Default" />
  <dt-badge type="info" text="Info" />
  <dt-badge type="success" text="Success" />
  <dt-badge type="warning" text="Warning" />
  <dt-badge type="critical" text="Critical" />
  <dt-badge type="bulletin" text="Bulletin" />
  <dt-badge type="ai" text="Ai" />
  <dt-badge kind="count" text="1" />
  <dt-badge kind="count" type="info" text="1" />
  <dt-badge kind="count" type="success" text="1" />
  <dt-badge kind="count" type="warning" text="1" />
  <dt-badge kind="count" type="critical" text="1" />
  <dt-badge kind="count" type="bulletin" text="1" />
</dt-stack>
</code-well-header>
<h3 id="label-badge-examples" tabindex="-1" class="d-docsite--header-3">Label Badge Examples <a class="header-anchor d-link d-docsite--link d-link" href="#label-badge-examples" aria-hidden="true">#</a></h3>
<code-well-header>
  <div>
    <dt-stack direction="row" gap="400" align="center">
      <span class="d-badge">Co-host</span>
      <span class="d-badge">Customer</span>
      <span class="d-badge">
        <span class="d-badge__icon-left">
          <dt-icon name="lock" size="200"/>
        </span>
        <span class="d-badge__label">Locked</span>
        </span>
      <span class="d-badge">
        <span class="d-badge__icon-left">
          <dt-icon name="message" size="200"/>
        </span>
        <span class="d-badge__label">Chat log</span>
      </span>
    </dt-stack>
  </div>
  <div>
    <dt-stack direction="row" gap="400" align="center">
      <span class="d-badge d-badge--info"><span class="d-badge__label">In progress</span></span>
      <span class="d-badge d-badge--info"><span class="d-badge__label">Beta</span></span>
      <span class="d-badge d-badge--info"><span class="d-badge__label">Draft</span></span>
    </dt-stack>
  </div>
  <div>
    <dt-stack direction="row" gap="400" align="center">
      <span class="d-badge d-badge--warning"><span class="d-badge__label">Overdue</span></span>
    </dt-stack>
  </div>
  <div>
    <dt-stack direction="row" gap="400" align="center">
      <span class="d-badge d-badge--success"><span class="d-badge__label">Resolved</span></span>
    </dt-stack>
  </div>
  <div>
    <dt-stack direction="row" gap="400" align="center">
      <span class="d-badge d-badge--critical">
        <span class="d-badge__icon-left">
          <dt-icon name="record-filled" size="200"/>
        </span>
        <span class="d-badge__label">Recording</span>
      </span>
    </dt-stack>
  </div>
  <div>
    <dt-stack direction="row" gap="400" align="center">
      <span class="d-badge d-badge--bulletin"><span class="d-badge__label">Live</span></span>
      <span class="d-badge d-badge--bulletin"><span class="d-badge__label">Presenter</span></span>
    </dt-stack>
  </div>
  <div>
    <dt-stack direction="row" gap="400" align="center">
      <span class="d-badge d-badge--ai">
        <span class="d-badge__icon-left">
          <dt-icon name="dialpad-ai" size="200"/>
        </span>
        <span class="d-vi-visible-sr">Ai</span>
        <span class="d-badge__label">Notes</span>
      </span>
      <span class="d-badge d-badge--ai">
        <span class="d-badge__icon-left">
          <dt-icon name="dialpad-ai" size="200"/>
        </span>
        <span class="d-vi-visible-sr">Ai</span>
        <span class="d-badge__label">Suggestion</span>
      </span>
      <span class="d-badge d-badge--ai">
        <span class="d-badge__icon-left">
          <dt-icon name="dialpad-ai" size="200"/>
        </span>
        <span class="d-vi-visible-sr">Ai</span>
        <span class="d-badge__label">enabled</span>
      </span>
      <span class="d-badge d-badge--ai">
        <span class="d-badge__icon-left">
          <dt-icon name="dialpad-ai" size="200"/>
        </span>
        <span class="d-vi-visible-sr">Ai</span>
        <span class="d-badge__label">Transcript</span>
      </span>
    </dt-stack>
  </div>
</code-well-header>
<h3 id="count-badge-examples" tabindex="-1" class="d-docsite--header-3">Count Badge Examples <a class="header-anchor d-link d-docsite--link d-link" href="#count-badge-examples" aria-hidden="true">#</a></h3>
<code-well-header>
  <div>
    <dt-stack direction="row" gap="400" align="center">
      <span class="d-badge d-badge--count d-badge--success">
        <span class="d-badge__icon-left">
          <dt-icon name="arrow-up" size="200"/>
        </span>
        <span class="d-badge__label">5%</span>
      </span>
    </dt-stack>
  </div>
  <div>
    <dt-stack direction="row" gap="400" align="center">
      <span class="d-badge d-badge--count d-badge--critical">
        <span class="d-badge__icon-left">
          <dt-icon name="arrow-down" size="200"/>
        </span>
        <span class="d-badge__label">-12%</span>
      </span>
    </dt-stack>
  </div>
  <div>
    <dt-stack direction="row" gap="400" align="center">
      <span class="d-badge d-badge--count d-badge--bulletin"><span class="d-badge__label">1</span></span>
      <span class="d-badge d-badge--count d-badge--bulletin"><span class="d-badge__label">18</span></span>
      <span class="d-badge d-badge--count d-badge--bulletin"><span class="d-badge__label">99+</span></span>
    </dt-stack>
  </div>
</code-well-header>
<h3 id="styling-documentation-changes" tabindex="-1" class="d-docsite--header-3">Styling &amp; documentation changes <a class="header-anchor d-link d-docsite--link d-link" href="#styling-documentation-changes" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Visual styling updates.</li>
<li class="d-docsite--list-element">You no longer set a badge to a specific color, you set it to a &quot;type&quot; with a specific semantic intent. See <a href="https://dialtone.dialpad.com/components/badge.html#type" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">badge documenatation</a> for all the possible types.</li>
<li class="d-docsite--list-element">Two different kinds of badge &quot;label&quot; which is meant for alphanumeric text and &quot;count&quot; which makes the badge circular and is intended for numerical text only.</li>
<li class="d-docsite--list-element">Improved documentation for badge usage and best practices.</li>
<li class="d-docsite--list-element">Support for icons on either side of the badge.</li>
</ul>
<h3 id="vue-component-changes" tabindex="-1" class="d-docsite--header-3">Vue component changes <a class="header-anchor d-link d-docsite--link d-link" href="#vue-component-changes" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">New iconLeft and iconRight prop on the vue component. pass in any icon name from the <a href="https://dialtone.dialpad.com/components/icon.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">icon catalog</a> to set the icon.</li>
<li class="d-docsite--list-element">New prop &quot;kind&quot;, set to &quot;label&quot; or &quot;count&quot;.</li>
<li class="d-docsite--list-element">New prop &quot;type&quot;, replaces &quot;color&quot;.</li>
<li class="d-docsite--list-element">Special case: setting the type to 'ai' will automatically display the AI icon on the left. This can be overridden if desired by setting the iconLeft prop.</li>
</ul>
<h2 id="avatar" tabindex="-1" class="d-docsite--header-2">Avatar <a class="header-anchor d-link d-docsite--link d-link" href="#avatar" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">We have also made some updates to the avatar component. You'll notice that when you use the component in initials or icon mode it will now display a randomly generated gradient for the background using multiple colors:</p>
<code-well-header>
  <dt-avatar>BP</dt-avatar>
</code-well-header>
<p class="d-docsite--paragraph">Very fancy!! 🧐 Thanks to Francis and Jose for this one.</p>
<p class="d-docsite--paragraph">The new presence component has also been released and is integrated into the Avatar. You'll notice there is a new prop on the <a href="https://dialtone.dialpad.com/vue/?path=/story/components-avatar--default" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Vue component</a> called presence in which you can set the presence state for the avatar.</p>
<code-well-header>
  <dt-avatar presence="away">BP</dt-avatar>
</code-well-header>
<h2 id="combobox" tabindex="-1" class="d-docsite--header-2">Combobox <a class="header-anchor d-link d-docsite--link d-link" href="#combobox" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">You may have noticed that while our input component had a label on it, none of our combobox components which use input had labels, or had any way of setting the label.</p>
<p class="d-docsite--paragraph">All combobox components now have a required label prop. You do not have to set a label that shows up visually, but if you don't then you still have to label it for screenreaders. You can do so by setting the label prop and then setting the labelVisible prop to <code v-pre>false</code> in which case the aria-label will still be set even if it is not shown visually.</p>
<p class="d-docsite--paragraph">That's all for today have a great weekend all!</p>
</BlogPost>
</div></template>


<script setup>
import BlogPost from '@baseComponents/BlogPost.vue';
import { parse } from 'date-fns';
</script>